import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetConfigV1ProbeLocationsConfigGetQueryKey,
  getListLocationsV1ProbeLocationsGetQueryKey,
  useCreateLocationV1ProbeLocationsPost,
  useDeleteLocationV1ProbeLocationsLocationIdDelete,
  useGetConfigV1ProbeLocationsConfigGet,
  useListLocationsV1ProbeLocationsGet,
  useMoveCityV1ProbeLocationsMoveCityPost,
  useSetEnabledV1ProbeLocationsSetEnabledPost,
  useUpdateConfigV1ProbeLocationsConfigPut,
  useUpdateLocationV1ProbeLocationsLocationIdPatch,
} from "../api/generated/qcsControlPlane";
import { ProbeTraversal, type ProbeConfigOut, type ProbeLocationOut } from "../api/generated/model";
import { ago } from "../api/labels";
import { useCanWrite } from "../auth/me";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { ProbePointPicker, type ContextPoint } from "../components/ProbePointPicker";
import { IconChevronDown, IconChevronUp, IconPlus, IconStores } from "../components/icons";
import { useToast } from "../components/toast";
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Panel,
  Row,
  Skeleton,
  SkeletonRegion,
  Stack,
} from "@akaike/ui";
import { Field, Seg } from "../components/foundation";

function errMsg(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === "string" && m) return m;
  }
  return fallback;
}

type Draft = {
  id?: string;
  city: string;
  label: string;
  lat: string;
  lon: string;
  rank: string;
  city_rank: string;
  enabled: boolean;
};

const emptyDraft = (city = "", city_rank = 0): Draft => ({
  city,
  label: "",
  lat: "",
  lon: "",
  rank: "0",
  city_rank: String(city_rank),
  enabled: true,
});

function toDraft(p: ProbeLocationOut): Draft {
  return {
    id: p.id,
    city: p.city,
    label: p.label,
    lat: String(p.lat),
    lon: String(p.lon),
    rank: String(p.rank ?? 0),
    city_rank: String(p.city_rank ?? 0),
    enabled: p.enabled ?? true,
  };
}

const num = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export function ProbeLocations() {
  const canWrite = useCanWrite();
  const qc = useQueryClient();
  const notify = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  useBreadcrumbTail([{ label: "Probe locations" }]);

  const pointsQuery = useListLocationsV1ProbeLocationsGet();
  const configQuery = useGetConfigV1ProbeLocationsConfigGet();
  const points = useMemo(() => pointsQuery.data ?? [], [pointsQuery.data]);

  const invalidatePoints = () =>
    qc.invalidateQueries({
      queryKey: getListLocationsV1ProbeLocationsGetQueryKey(),
    });
  const invalidateConfig = () =>
    qc.invalidateQueries({
      queryKey: getGetConfigV1ProbeLocationsConfigGetQueryKey(),
    });

  const create = useCreateLocationV1ProbeLocationsPost({
    mutation: {
      onSuccess: () => {
        notify("Probe point added");
        setDraft(null);
        invalidatePoints();
      },
      onError: (e) => notify(errMsg(e, "Add failed"), "crit"),
    },
  });
  const update = useUpdateLocationV1ProbeLocationsLocationIdPatch({
    mutation: {
      onSuccess: () => {
        notify("Probe point saved");
        setDraft(null);
        invalidatePoints();
      },
      onError: (e) => notify(errMsg(e, "Save failed"), "crit"),
    },
  });
  const remove = useDeleteLocationV1ProbeLocationsLocationIdDelete({
    mutation: {
      onSuccess: () => {
        notify("Probe point removed");
        invalidatePoints();
      },
      onError: (e) => notify(errMsg(e, "Delete failed"), "crit"),
    },
  });
  const setEnabled = useSetEnabledV1ProbeLocationsSetEnabledPost({
    mutation: {
      onSuccess: () => invalidatePoints(),
      onError: (e) => notify(errMsg(e, "Update failed"), "crit"),
    },
  });
  const moveCity = useMoveCityV1ProbeLocationsMoveCityPost({
    mutation: {
      onSuccess: () => invalidatePoints(),
      onError: (e) => notify(errMsg(e, "Reorder failed"), "crit"),
    },
  });

  const busy =
    create.isPending ||
    update.isPending ||
    remove.isPending ||
    setEnabled.isPending ||
    moveCity.isPending;

  // Group points by city, preserving city_rank then city then rank order.
  const groups = useMemo(() => {
    const sorted = [...points].sort(
      (a, b) =>
        (a.city_rank ?? 0) - (b.city_rank ?? 0) ||
        a.city.localeCompare(b.city) ||
        (a.rank ?? 0) - (b.rank ?? 0),
    );
    const out: {
      city: string;
      city_rank: number;
      items: ProbeLocationOut[];
    }[] = [];
    for (const p of sorted) {
      const last = out[out.length - 1];
      if (last && last.city === p.city) last.items.push(p);
      else out.push({ city: p.city, city_rank: p.city_rank ?? 0, items: [p] });
    }
    return out;
  }, [points]);

  // FLIP: when the city order changes (up/down arrows), slide each group from its old
  // position to its new one instead of snapping. We record each group's top before the
  // re-render and, right after the DOM updates, transform it back then transition to 0.
  const groupEls = useRef(new Map<string, HTMLDivElement>());
  const prevTops = useRef(new Map<string, number>());
  useLayoutEffect(() => {
    const tops = new Map<string, number>();
    for (const [city, el] of groupEls.current) {
      const top = el.getBoundingClientRect().top;
      tops.set(city, top);
      const prev = prevTops.current.get(city);
      if (prev !== undefined && prev !== top) {
        el.style.transition = "none";
        el.style.transform = `translateY(${prev - top}px)`;
        requestAnimationFrame(() => {
          el.style.transition = "transform 320ms cubic-bezier(.2,.7,.2,1)";
          el.style.transform = "";
        });
      }
    }
    prevTops.current = tops;
  }, [groups]);

  const context: ContextPoint[] = points.map((p) => ({
    id: p.id,
    lat: p.lat,
    lon: p.lon,
    label: `${p.city} · ${p.label}`,
    enabled: p.enabled ?? true,
  }));

  const pickerValue =
    draft && num(draft.lat) !== null && num(draft.lon) !== null
      ? { lat: num(draft.lat) as number, lon: num(draft.lon) as number }
      : null;

  function onPick(v: { lat: number; lon: number }) {
    setDraft((d) => {
      const base = d ?? emptyDraft();
      return { ...base, lat: String(v.lat), lon: String(v.lon) };
    });
  }

  function submit() {
    if (!draft) return;
    const lat = num(draft.lat);
    const lon = num(draft.lon);
    if (!draft.city.trim() || !draft.label.trim() || lat === null || lon === null) {
      notify("City, label, lat and lon are required", "crit");
      return;
    }
    const body = {
      city: draft.city.trim(),
      label: draft.label.trim(),
      lat,
      lon,
      rank: num(draft.rank) ?? 0,
      city_rank: num(draft.city_rank) ?? 0,
      enabled: draft.enabled,
    };
    if (draft.id) update.mutate({ locationId: draft.id, data: body });
    else create.mutate({ data: body });
  }

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Probe locations"
        description="Manage the city probe points and walk settings used by brand-search sweeps."
      />

      <TuningPanel
        canWrite={canWrite}
        config={configQuery.data}
        isError={configQuery.isError}
        onSaved={invalidateConfig}
      />

      <Row gap="md" align="start" wrap>
        <Stack gap="md" style={{ flex: "1 1 420px", minWidth: 320 }}>
          <Panel>
            <Panel.Head title="Probe points">
              {groups.length > 0 && (
                <span className="hint mono" style={{ marginLeft: 10 }}>
                  {points.length} points · {groups.length} cities
                </span>
              )}
              <div className="flex-1" />
              {canWrite && <Button
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={() => setDraft(emptyDraft("", groups.length))}
              >
                <IconPlus />
                Add point
              </Button>}
            </Panel.Head>

            {pointsQuery.isLoading && !pointsQuery.data ? (
              <Panel.Body tight>
                <SkeletonRegion label="Loading probe points">
                  <Stack gap="sm" className="p-4">
                    {Array.from({ length: 3 }, (_, i) => (
                      <Skeleton key={i} h={28} />
                    ))}
                  </Stack>
                </SkeletonRegion>
              </Panel.Body>
            ) : pointsQuery.isError ? (
              <Panel.Body>
                <ErrorState
                  compact
                  title="Could not load probe points"
                  message="Is the control plane up?"
                  onRetry={() => void pointsQuery.refetch()}
                />
              </Panel.Body>
            ) : groups.length === 0 ? (
              <Panel.Body>
                <EmptyState compact title="No probe points yet">
                  The brand-search test falls back to the built-in city fixture. Add a point to
                  override it.
                </EmptyState>
              </Panel.Body>
            ) : (
              <Panel.Body tight>
                {groups.map((g, gi) => (
                  <div
                    className="list-group"
                    key={g.city}
                    ref={(el) => {
                      if (el) groupEls.current.set(g.city, el);
                      else groupEls.current.delete(g.city);
                    }}
                  >
                    <div className="list-ghead">
                      <span className="ico-14" style={{ color: "var(--brand)" }}>
                        <IconStores />
                      </span>
                      <b>{g.city}</b>
                      {canWrite && <div className="reorder">
                        <Button
                          variant="outline"
                          size="sm"
                          icon
                          disabled={busy || gi === 0}
                          title="Move city up"
                          onClick={() =>
                            moveCity.mutate({
                              data: { city: g.city, direction: "up" },
                            })
                          }
                        >
                          <IconChevronUp />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          icon
                          disabled={busy || gi === groups.length - 1}
                          title="Move city down"
                          onClick={() =>
                            moveCity.mutate({
                              data: { city: g.city, direction: "down" },
                            })
                          }
                        >
                          <IconChevronDown />
                        </Button>
                      </div>}
                      <span className="hint mono">
                        {g.items.length}
                        {g.items.length === 1 ? " pt" : " pts"}
                      </span>
                      <div className="flex-1" />
                      {canWrite && <Button variant="outline" size="sm" disabled={busy} onClick={() => setDraft(emptyDraft(g.city, g.city_rank))}>
                        <IconPlus />
                        Add
                      </Button>}
                    </div>
                    {g.items.map((p) => (
                      <div
                        key={p.id}
                        className="list-row"
                        style={{ opacity: p.enabled === false ? 0.5 : 1 }}
                      >
                        <span className="mono faint" style={{ width: 12, fontSize: 12 }}>
                          {p.rank ?? 0}
                        </span>
                        <b style={{ minWidth: 116, fontWeight: 500 }}>{p.label}</b>
                        <span className="mono hint" style={{ fontSize: 12 }}>
                          {p.lat.toFixed(4)}, {p.lon.toFixed(4)}
                        </span>
                        <div className="flex-1" />
                        {canWrite && <>
                        <Button
                          variant={p.enabled === false ? "success" : "outline"}
                          size="sm"
                          disabled={busy}
                          onClick={() =>
                            setEnabled.mutate({
                              data: {
                                enabled: !(p.enabled ?? true),
                                ids: [p.id],
                              },
                            })
                          }
                        >
                          {p.enabled === false ? "Enable" : "Disable"}
                        </Button>
                        <Button variant="outline" size="sm" disabled={busy} onClick={() => setDraft(toDraft(p))}>
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={busy}
                          onClick={() => remove.mutate({ locationId: p.id })}
                        >
                          Delete
                        </Button>
                        </>}
                      </div>
                    ))}
                  </div>
                ))}
              </Panel.Body>
            )}
          </Panel>
        </Stack>

        <Stack
          gap="sm"
          style={{
            flex: "1 1 420px",
            minWidth: 320,
            position: "sticky",
            top: 12,
          }}
        >
          <Panel>
            <Panel.Head title={draft ? (draft.id ? "Edit point" : "New point") : "Map"}>
              {draft && (
                <>
                  <div className="flex-1" />
                  <Button variant="outline" size="sm" onClick={() => setDraft(null)}>
                    Cancel
                  </Button>
                </>
              )}
            </Panel.Head>
            <Panel.Body className="col gap-3">
              <ProbePointPicker
                value={pickerValue}
                context={context}
                excludeId={draft?.id ?? null}
                onPick={canWrite ? onPick : () => undefined}
              />
              {draft ? (
                <Stack gap="sm">
                  <Row gap="sm" wrap>
                    <Field label="City" className="grow basis-40">
                      <Input
                        value={draft.city}
                        placeholder="Bengaluru"
                        onChange={(e) => setDraft({ ...draft, city: e.currentTarget.value })}
                      />
                    </Field>
                    <Field label="Label" className="grow basis-40">
                      <Input
                        value={draft.label}
                        placeholder="Koramangala"
                        onChange={(e) => setDraft({ ...draft, label: e.currentTarget.value })}
                      />
                    </Field>
                  </Row>
                  <Row gap="sm" wrap>
                    <Field label="Latitude" className="grow basis-30">
                      <Input
                        value={draft.lat}
                        placeholder="12.9352"
                        onChange={(e) => setDraft({ ...draft, lat: e.currentTarget.value })}
                      />
                    </Field>
                    <Field label="Longitude" className="grow basis-30">
                      <Input
                        value={draft.lon}
                        placeholder="77.6245"
                        onChange={(e) => setDraft({ ...draft, lon: e.currentTarget.value })}
                      />
                    </Field>
                  </Row>
                  <Row gap="sm" wrap>
                    <Field label="Rank (in city)" className="grow basis-25">
                      <Input
                        value={draft.rank}
                        onChange={(e) => setDraft({ ...draft, rank: e.currentTarget.value })}
                      />
                    </Field>
                    <Field label="Enabled" className="grow basis-30">
                      <Seg
                        value={draft.enabled ? "on" : "off"}
                        options={[
                          { value: "on", label: "on" },
                          { value: "off", label: "off" },
                        ]}
                        onChange={(v) => setDraft({ ...draft, enabled: v === "on" })}
                      />
                    </Field>
                  </Row>
                  <Button
                    variant="primary"
                    className="justify-center"
                    disabled={busy}
                    onClick={submit}
                  >
                    {draft.id ? "Save point" : "Add point"}
                  </Button>
                </Stack>
              ) : (
                <span className="hint">
                  {canWrite
                    ? "Click a point in the list to edit it, or click the map to place a new one."
                    : "Read-only: probe points are listed on the left and plotted on the map."}
                </span>
              )}
            </Panel.Body>
          </Panel>
        </Stack>
      </Row>
    </>
  );
}

function TuningPanel({
  canWrite,
  config,
  isError,
  onSaved,
}: {
  canWrite: boolean;
  config: ProbeConfigOut | undefined;
  isError: boolean;
  onSaved: () => void;
}) {
  const notify = useToast();
  const [traversal, setTraversal] = useState<ProbeTraversal>(ProbeTraversal.rank_major);
  const [maxKm, setMaxKm] = useState("");
  // Track the config identity we last synced from so edits aren't clobbered by refetch.
  const [synced, setSynced] = useState<string | null>(null);

  const key = config ? `${config.traversal}|${config.max_km}|${config.updated_at}` : null;
  if (config && key !== synced) {
    setTraversal(config.traversal);
    setMaxKm(String(config.max_km));
    setSynced(key);
  }

  const save = useUpdateConfigV1ProbeLocationsConfigPut({
    mutation: {
      onSuccess: () => {
        notify("Probe tuning saved");
        onSaved();
      },
      onError: (e) => notify(errMsg(e, "Save failed"), "crit"),
    },
  });

  // Enable the save button only when the form differs from the saved config.
  const dirty =
    !!config && (traversal !== config.traversal || num(maxKm) !== config.max_km);

  function submit() {
    const mk = num(maxKm);
    if (mk === null || mk <= 0) {
      notify("Max km must be greater than 0", "crit");
      return;
    }
    save.mutate({ data: { traversal, max_km: mk } });
  }

  return (
    <Panel className="mb-3.5">
      <Panel.Head title="Walk tuning">
        {config?.updated_by && (
          <span className="hint" style={{ marginLeft: 8 }}>
            last edited by {config.updated_by} {ago(config.updated_at)}
          </span>
        )}
      </Panel.Head>
      <Panel.Body>
        {isError ? (
          <ErrorState compact title="Could not load probe tuning" />
        ) : (
          <Row gap="md" align="end" wrap>
            <Field label="Traversal" className="basis-55">
              {canWrite ? (
                <Seg
                  value={traversal}
                  options={[
                    { value: "rank_major", label: "rank-major" },
                    { value: "city_major", label: "city-major" },
                  ]}
                  onChange={setTraversal}
                />
              ) : (
                <span>{traversal === "city_major" ? "city-major" : "rank-major"}</span>
              )}
            </Field>
            <Field label="Max km" className="basis-[150px]">
              {canWrite ? (
                <Input value={maxKm} onChange={(e) => setMaxKm(e.currentTarget.value)} />
              ) : (
                <span className="num">{maxKm}</span>
              )}
            </Field>
            <div className="flex-1" />
            {canWrite && (
              <Button
                variant="primary"
                disabled={save.isPending || !dirty}
                title={dirty ? "Save your changes" : "No changes to save"}
                onClick={submit}
              >
                {save.isPending ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </Button>
            )}
          </Row>
        )}
        <p className="hint" style={{ marginTop: 10, marginBottom: 0, maxWidth: "72ch" }}>
          Searches every point until the brand turns up. <b>Traversal</b> sets the order -
          rank-major spreads across cities first, city-major goes city by city. <b>Max km</b> skips
          a point with no store nearby.
        </p>
      </Panel.Body>
    </Panel>
  );
}
